import { MapPin, Phone, Mail, Clock, MessageCircle, Navigation } from "lucide-react";
import Seo from "@/components/Seo";
import EnquiryForm from "@/components/EnquiryForm";
import CostEstimator from "@/components/CostEstimator";
import { useLang } from "@/i18n/LanguageContext";
import { BUSINESS, mapsEmbedUrl, mapsDirectionsUrl, whatsappUrl, pick } from "@/data/site";

export default function Contact() {
  const { t, lang } = useLang();
  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact Us — V.B. Sharada Associates | Tandur, Telangana"
        description="Contact V.B. Sharada Associates for contracting, planning, construction and interior projects in Tandur, Telangana. Visit our office on Hyderabad Road or send an enquiry."
      />

      <section className="warm-grid border-b border-orange-100">
        <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-14 md:py-20">
          <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 font-semibold text-xs uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            {t("con.eyebrow")}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 max-w-3xl">
            {t("con.h1")}
          </h1>
          <p className="mt-4 text-base text-slate-600 leading-relaxed max-w-2xl">{t("con.para")}</p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3">
            <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">{t("con.formH2")}</h2>
            <EnquiryForm />
          </div>
          <aside className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-teal-100 rounded-2xl shadow-lg shadow-teal-600/5 p-6">
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-5">{t("con.officeH3")}</h3>
              <ul className="space-y-4 text-sm">
                <li className="flex gap-3">
                  <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">
                    <MapPin className="w-4 h-4 text-orange-600" />
                  </div>
                  <span className="text-slate-600 leading-relaxed mt-1.5" data-testid="contact-address">
                    {BUSINESS.address}
                  </span>
                </li>
                <li className="flex gap-3 items-center">
                  <div className="w-9 h-9 rounded-xl bg-teal-100 flex items-center justify-center shrink-0">
                    <Phone className="w-4 h-4 text-teal-600" />
                  </div>
                  <a href={`tel:${BUSINESS.phone.replace(/\s/g, "")}`} className="text-slate-600 hover:text-teal-700" data-testid="contact-phone">
                    {BUSINESS.phone} / {BUSINESS.phoneAlt}
                  </a>
                </li>
                <li className="flex gap-3 items-center">
                  <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                    <Mail className="w-4 h-4 text-amber-600" />
                  </div>
                  <a href={`mailto:${BUSINESS.email}`} className="text-slate-600 hover:text-amber-700 break-all" data-testid="contact-email">
                    {BUSINESS.email}
                  </a>
                </li>
                <li className="flex gap-3 items-center">
                  <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center shrink-0">
                    <Clock className="w-4 h-4 text-purple-600" />
                  </div>
                  <span className="text-slate-600">{pick(BUSINESS, "hours", lang)}</span>
                </li>
              </ul>
              <div className="mt-6 grid grid-cols-1 gap-3">
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-button"
                  className="inline-flex items-center justify-center gap-2 bg-emerald-500 text-white font-semibold px-5 py-3 rounded-full hover:bg-emerald-600 transition-colors duration-200 text-sm shadow-md shadow-emerald-500/30"
                >
                  <MessageCircle className="w-4 h-4" /> {t("con.whatsappBtn")}
                </a>
                <a
                  href={mapsDirectionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-directions-button"
                  className="inline-flex items-center justify-center gap-2 border-2 border-teal-600 text-teal-700 font-semibold px-5 py-3 rounded-full hover:bg-teal-50 transition-colors duration-200 text-sm"
                >
                  <Navigation className="w-4 h-4" /> {t("con.directionsBtn")}
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="pb-16 md:pb-24">
        <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto">
          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">{t("con.mapH2")}</h2>
          <div className="border-4 border-white rounded-3xl overflow-hidden shadow-xl shadow-orange-500/10">
            <iframe
              title="V.B. Sharada Associates office location — Hyderabad Road, Tandur"
              data-testid="google-maps-embed"
              src={mapsEmbedUrl}
              className="w-full h-[360px] sm:h-[420px] border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      <section className="pb-16 md:pb-24">
        <div className="px-4 sm:px-8 lg:px-12 max-w-5xl mx-auto">
          <CostEstimator />
        </div>
      </section>
    </div>
  );
}
